import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@cbrebuild/blocks';

/**
 * Component to conditionally show Voucher Access button based on deal type restrictions
 * 
 * Logic:
 * 1. If connector_id exists → Show button (current process)
 * 2. If connector_id doesn't exist BUT salesforce_id exists → Show '--'
 * 3. If neither exist → Check restrictions:
 *    - Use passed dealRestrictions prop from parent
 *    - If ANY user has deal_type in their restricted_deal_types → Show button
 *    - Otherwise → Show '--'
 */
const VoucherAccessConditionalButton = ({ 
  deal, 
  showAccess,
  onToggle,
  dealUsers, 
  dealTeams,
  dealRestrictions = null,
  restrictionsLoading = false
}) => {
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const [eligibleUserIds, setEligibleUserIds] = useState([]);

  // Extract all team member user IDs
  const getAllTeamMemberIds = () => {
    const userIds = new Set();
    
    if (Array.isArray(dealUsers)) {
      dealUsers.forEach(userItem => {
        if (userItem?.user?.id) {
          userIds.add(userItem.user.id);
        }
      });
    }
    
    if (Array.isArray(dealTeams)) {
      dealTeams.forEach(teamItem => {
        if (Array.isArray(teamItem?.team?.users)) {
          teamItem.team.users.forEach(user => {
            if (user?.id) {
              userIds.add(user.id);
            }
          });
        }
      });
    }
    
    return Array.from(userIds);
  };

  useEffect(() => {
    const checkRestrictions = () => {
      // Case 1: Has connector_id - always show button
      if (deal.connector_id) {
        setShouldShowButton(true);
        return;
      }

      // Case 2: Has salesforce_id but no connector_id - don't show button
      if (deal.salesforce_id || deal.pursuit_id) {
        setShouldShowButton(false);
        return;
      }

      // Case 3: Check restrictions using passed prop
      if (!dealRestrictions) {
        // Still loading or no data yet
        setShouldShowButton(false);
        return;
      }

      const userIds = getAllTeamMemberIds();
      
      if (userIds.length === 0) {
        setShouldShowButton(false);
        return;
      }

      // Check if ANY user has this deal_type in their restricted_deal_types
      const matchedUserIds = [];
      let foundMatch = false;

      userIds.forEach(userId => {
        const restrictedTypes = dealRestrictions[userId] || [];
        
        // If this user has the current deal_type in restrictions
        if (restrictedTypes.includes(deal.deal_type)) {
          foundMatch = true;
          matchedUserIds.push(userId);
        }
      });

      setShouldShowButton(foundMatch);
      setEligibleUserIds(matchedUserIds);
    };

    checkRestrictions();
  }, [deal.id, deal.connector_id, deal.salesforce_id, deal.pursuit_id, deal.deal_type, dealUsers, dealTeams, dealRestrictions]);

  if (restrictionsLoading) {
    return <Icon iconName="spinner" spin />;
  }

  if (!shouldShowButton) {
    return <span>--</span>;
  }

  return (
    <button
      className="linked-mta-transaction-btn"
      onClick={onToggle}
      title={showAccess ? 'Hide voucher access' : 'Show voucher access'}
      data-eligible-users={eligibleUserIds.join(',')}
    >
      <Icon iconName={showAccess ? 'chevron-up' : 'key'} />
      {showAccess ? 'Hide Access' : 'Manage Access'}
    </button>
  );
};

VoucherAccessConditionalButton.propTypes = {
  deal: PropTypes.shape({
    id: PropTypes.number.isRequired,
    connector_id: PropTypes.string,
    salesforce_id: PropTypes.string,
    pursuit_id: PropTypes.string,
    deal_type: PropTypes.string.isRequired,
  }).isRequired,
  showAccess: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  dealUsers: PropTypes.array,
  dealTeams: PropTypes.array,
  dealRestrictions: PropTypes.object,
  restrictionsLoading: PropTypes.bool,
};

VoucherAccessConditionalButton.defaultProps = {
  dealUsers: [],
  dealTeams: [],
  dealRestrictions: null,
  restrictionsLoading: false,
};

export default VoucherAccessConditionalButton;
